import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI, clientsAPI, usersAPI, opportunitiesAPI, reportsAPI } from '../lib/api';
import { ClientCombobox } from '../components/ClientCombobox';
import { useAuth } from '../context/AuthContext';
import { buildSellerColors, initials } from '../components/pipeline/sellerColors';
import { WeekProductionColumn } from '../components/calendar/WeekProductionColumn';
import { LateBadge } from '../components/production/LateBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Plus,
    Phone,
    Mail,
    Users,
    ClipboardList,
    CalendarIcon,
    Clock,
    CheckCircle2,
    Truck,
    Printer,
    Store,
    Loader2,
    Trash2,
    Download,
    Plane,
    Palmtree,
    NotebookPen,
    Factory
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { it } from 'date-fns/locale';

const ACTIVITY_TYPES = [
    { id: 'meeting', label: 'Fiera', icon: Store, color: '#7B1FA2' },
    { id: 'task', label: 'Attività', icon: ClipboardList, color: '#F57C00' },
    { id: 'viaggio', label: 'Viaggio', icon: Plane, color: '#0891B2' },
    { id: 'ferie', label: 'Ferie', icon: Palmtree, color: '#16A34A' },
    { id: 'delivery', label: 'Consegna', icon: Truck, color: '#DC2626' },
    { id: 'confirmation', label: 'Offerta confermata', icon: CheckCircle2, color: '#2563EB' },
    { id: 'production', label: 'Inizio produzione', icon: Factory, color: '#064E3B' },
    // Tipi legacy: mantenuti solo per icone/colori dei dati esistenti (non selezionabili)
    { id: 'call', label: 'Chiamata', icon: Phone, color: '#1976D2' },
    { id: 'email', label: 'Email', icon: Mail, color: '#388E3C' },
];

// Tipi selezionabili nel form "Nuova attività"
const CREATABLE_TYPES = ['meeting', 'task', 'viaggio', 'ferie', 'delivery', 'confirmation', 'production'];

// Eventi automatici generati dalle offerte, colorati sempre per tipo
const AUTO_EVENT_TYPES = ['delivery', 'confirmation', 'production'];

// Tipi sempre colorati con il colore del tipo (per distinguerli a colpo d'occhio)
const TYPE_COLORED_TYPES = ['delivery', 'confirmation', 'production', 'viaggio', 'ferie'];

const CalendarPage = () => {
    const { user, isAdmin, isBackoffice, isAreaManager, isAddettoConsegna } = useAuth();
    const canSeeOwners = isAdmin() || isBackoffice() || isAreaManager();
    const canAssign = isAdmin() || isBackoffice();
    const canWriteRelazione = ['admin', 'commerciale', 'area_manager'].includes(user?.role);
    const openRelazione = (a) => {
        const opp = a.opportunity_id ? opportunities.find(o => o.id === a.opportunity_id) : null;
        const clientId = a.client_id || opp?.client_id || null;
        const client = clients.find(c => c.id === clientId);
        const d = a.scheduled_at ? new Date(a.scheduled_at) : null;
        navigate('/relazioni', { state: { prefill: {
            title: AUTO_EVENT_TYPES.includes(a.type) ? `${a.title}` : `${a.title}${client ? ` — ${client.company_name}` : ''}`,
            country: client?.country || '',
            client_ids: clientId ? [clientId] : [],
            opportunity_id: a.opportunity_id || '',
            date_from: d ? format(d, 'yyyy-MM-dd') : '',
            date_to: a.end_at ? format(new Date(a.end_at), 'yyyy-MM-dd') : (d ? format(d, 'yyyy-MM-dd') : ''),
        } } });
    };
    const navigate = useNavigate();
    const openOffer = (oppId) => {
        if (oppId) navigate(`/pipeline?offer=${oppId}`);
    };
    const [activities, setActivities] = useState([]);
    const [clients, setClients] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [sellerColorMap, setSellerColorMap] = useState({});
    const [sellerFilter, setSellerFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState(isAddettoConsegna() ? 'delivery' : 'all');
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'
    const [paymentFilter, setPaymentFilter] = useState('all'); // all | saldato | acconto | nonsaldato
    const [weekSection, setWeekSection] = useState('all'); // all | delivery | confirmation | production
    const showSec = (t) => weekSection === 'all' || weekSection === t;
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        type: 'meeting',
        client_id: '',
        assigned_to: '',
        scheduled_at: new Date(),
        end_at: null,
        scheduled_time: '09:00',
        notes: ''
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [activitiesRes, clientsRes, sellersRes, oppsRes] = await Promise.all([
                activitiesAPI.getAll(),
                clientsAPI.getAll(),
                usersAPI.getSellers(),
                opportunitiesAPI.getAll(),
            ]);
            setActivities(activitiesRes.data);
            setClients(clientsRes.data);
            setOpportunities(oppsRes.data || []);
            setAllUsers(sellersRes.data || []);
            const sellerList = (sellersRes.data || []).filter(u => u.role !== 'addetto_consegna');
            setSellers(sellerList);
            setSellerColorMap(buildSellerColors(sellerList));
        } catch (error) {
            toast.error('Errore nel caricamento');
        } finally {
            setLoading(false);
        }
    };

    const getSellerName = (id) => allUsers.find(s => s.id === id)?.name || '';

    const paymentInfo = (status) => {
        if (status === 'saldato') return { label: 'Saldato', color: '#10B981' };
        if (status === 'acconto') return { label: 'Acconto', color: '#F59E0B' };
        return { label: 'Non saldato', color: '#6B7280' };
    };

    const matchesPayment = (opp) => {
        if (paymentFilter === 'all') return true;
        const ps = opp?.payment_status || '';
        if (paymentFilter === 'nonsaldato') return !ps;
        return ps === paymentFilter;
    };

    const formatCurrency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

    const activityColor = (activity) => {
        // Consegne/vendite e ferie/viaggi: sempre colore per tipo, distinto
        if (TYPE_COLORED_TYPES.includes(activity.type)) {
            return getActivityTypeInfo(activity.type).color;
        }
        if (canSeeOwners && sellerColorMap[activity.assigned_to]) {
            return sellerColorMap[activity.assigned_to];
        }
        return getActivityTypeInfo(activity.type).color;
    };

    const resetForm = () => {
        setEditingActivity(null);
        setFormData({
            title: '',
            type: 'meeting',
            client_id: '',
            assigned_to: '',
            scheduled_at: selectedDate,
            end_at: null,
            scheduled_time: '09:00',
            notes: ''
        });
    };

    const handleOpenDialog = () => {
        setEditingActivity(null);
        setFormData(prev => ({
            ...prev,
            scheduled_at: selectedDate,
            end_at: null
        }));
        setIsDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const [hours, minutes] = formData.scheduled_time.split(':');
            const scheduledAt = new Date(formData.scheduled_at);
            scheduledAt.setHours(parseInt(hours), parseInt(minutes), 0, 0);

            let endAtIso = null;
            if (formData.end_at) {
                const endAt = new Date(formData.end_at);
                endAt.setHours(23, 59, 0, 0);
                if (endAt >= scheduledAt) {
                    endAtIso = endAt.toISOString();
                }
            }

            const data = {
                title: formData.title,
                type: formData.type,
                client_id: formData.client_id || null,
                assigned_to: canAssign ? (formData.assigned_to || undefined) : undefined,
                scheduled_at: scheduledAt.toISOString(),
                end_at: endAtIso,
                notes: formData.notes || null
            };

            if (editingActivity) {
                await activitiesAPI.update(editingActivity.id, data);
                toast.success('Attività aggiornata');
            } else {
                await activitiesAPI.create(data);
                toast.success('Attività creata');
            }
            setIsDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        }
    };

    const handleToggleComplete = async (activity) => {
        try {
            await activitiesAPI.update(activity.id, { completed: !activity.completed });
            toast.success(activity.completed ? 'Attività riaperta' : 'Attività completata');
            loadData();
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        }
    };

    const handleDelete = async (activityId) => {
        if (!window.confirm('Eliminare questa attività?')) return;
        try {
            await activitiesAPI.delete(activityId);
            toast.success('Attività eliminata');
            loadData();
        } catch (error) {
            toast.error('Errore nell\'eliminazione');
        }
    };

    const clientNameById = useMemo(() => {
        const m = new Map();
        clients.forEach(c => m.set(c.id, c.company_name));
        return m;
    }, [clients]);

    const oppById = useMemo(() => {
        const m = new Map();
        opportunities.forEach(o => m.set(o.id, o));
        return m;
    }, [opportunities]);

    const getClientName = (clientId) => {
        return clientNameById.get(clientId) || null;
    };

    const getActivityTypeInfo = (type) => {
        return ACTIVITY_TYPES.find(t => t.id === type) || ACTIVITY_TYPES[0];
    };

    const [detailActivity, setDetailActivity] = useState(null);
    const [editingActivity, setEditingActivity] = useState(null);
    const openDetail = (activity) => setDetailActivity(activity);

    const openEditActivity = (activity) => {
        const start = new Date(activity.scheduled_at);
        setEditingActivity(activity);
        setFormData({
            title: activity.title || '',
            type: activity.type || 'meeting',
            client_id: activity.client_id || '',
            assigned_to: activity.assigned_to || '',
            scheduled_at: start,
            end_at: activity.end_at ? new Date(activity.end_at) : null,
            scheduled_time: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
            notes: activity.notes || ''
        });
        setIsDialogOpen(true);
    };

    const getActivitiesForDate = (date) => {
        const d0 = new Date(date); d0.setHours(0, 0, 0, 0);
        return activities.filter(a => {
            if (canSeeOwners && sellerFilter !== 'all' && a.assigned_to !== sellerFilter) return false;
            if (typeFilter !== 'all' && a.type !== typeFilter) return false;
            const s = new Date(a.scheduled_at); s.setHours(0, 0, 0, 0);
            const e = a.end_at ? new Date(a.end_at) : new Date(a.scheduled_at);
            e.setHours(0, 0, 0, 0);
            return d0 >= s && d0 <= e;
        });
    };

    const daysInMonth = eachDayOfInterval({
        start: startOfMonth(currentMonth),
        end: endOfMonth(currentMonth)
    });

    const weekStart = startOfWeek(currentMonth, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    // Note da mostrare in vista settimanale: note dell'offerta, altrimenti note manuali dell'attività
    const weekNotes = (a, opp) => {
        const oppNotes = (opp?.description || '').trim();
        const actNotes = (a?.notes || '').trim();
        if (oppNotes) return oppNotes;
        if (actNotes && !/generat[ao] automaticamente/i.test(actNotes)) return actNotes;
        return '';
    };

    const printWeekReport = () => {
        const esc = (s) => String(s ?? '—').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
        const rangeLabel = `${format(weekStart, 'd MMM', { locale: it })} - ${format(endOfWeek(currentMonth, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: it })}`;
        let totalValue = 0, totalDeliveries = 0, totalSales = 0, totalProductions = 0;
        const sellerTotals = new Map();
        const daysHtml = weekDays.map(day => {
            const acts = getActivitiesForDate(day);
            const deliveries = showSec('delivery') ? acts.filter(a => a.type === 'delivery' && matchesPayment(oppById.get(a.opportunity_id))) : [];
            const sales = showSec('confirmation') ? acts.filter(a => a.type === 'confirmation' && matchesPayment(oppById.get(a.opportunity_id))) : [];
            const productions = showSec('production') ? acts.filter(a => a.type === 'production' && matchesPayment(oppById.get(a.opportunity_id))) : [];
            totalDeliveries += deliveries.length;
            totalSales += sales.length;
            totalProductions += productions.length;
            sales.forEach(a => {
                const v = oppById.get(a.opportunity_id)?.value || 0;
                totalValue += v;
                const id = a.assigned_to || 'none';
                const cur = sellerTotals.get(id) || { count: 0, value: 0 };
                cur.count += 1; cur.value += v;
                sellerTotals.set(id, cur);
            });
            if (deliveries.length === 0 && sales.length === 0 && productions.length === 0) return '';
            const row = (a, isSale, isProd = false) => {
                const o = oppById.get(a.opportunity_id) || {};
                const p = paymentInfo(o.payment_status);
                const cells = [
                    `<td>${esc(o.title || a.title)}</td>`,
                    `<td>${esc(o.commessa)}</td>`,
                    `<td>${esc(o.telaio)}</td>`,
                    isSale ? `<td>${esc(getSellerName(a.assigned_to))}</td>` : '',
                    `<td>${esc(getClientName(a.client_id))}</td>`,
                    isProd ? `<td>${esc(o.modello)}</td>` : `<td><span class="pill" style="background:${p.color}">${p.label}</span></td>`,
                    `<td class="notes">${esc(weekNotes(a, o))}</td>`,
                ].join('');
                return `<tr>${cells}</tr>`;
            };
            const delTable = deliveries.length ? `<h4 class="del">Consegne (${deliveries.length})</h4>
                <table><thead><tr><th>Veicolo</th><th>Commessa</th><th>Telaio</th><th>Cliente</th><th>Pagamento</th><th>Note</th></tr></thead>
                <tbody>${deliveries.map(a => row(a, false)).join('')}</tbody></table>` : '';
            const saleTable = sales.length ? `<h4 class="sale">Offerte confermate (${sales.length})</h4>
                <table><thead><tr><th>Veicolo</th><th>Commessa</th><th>Telaio</th><th>Venditore</th><th>Cliente</th><th>Pagamento</th><th>Note</th></tr></thead>
                <tbody>${sales.map(a => row(a, true)).join('')}</tbody></table>` : '';
            const prodTable = productions.length ? `<h4 class="prod">Inizio produzione (${productions.length})</h4>
                <table><thead><tr><th>Veicolo</th><th>Commessa</th><th>Telaio</th><th>Venditore</th><th>Cliente</th><th>Modello</th><th>Note</th></tr></thead>
                <tbody>${productions.map(a => row(a, true, true)).join('')}</tbody></table>` : '';
            return `<section><h3>${esc(format(day, 'EEEE d MMMM', { locale: it }))}</h3>${delTable}${saleTable}${prodTable}</section>`;
        }).join('');
        const sellerRows = Array.from(sellerTotals.entries())
            .map(([id, v]) => ({ name: id === 'none' ? '—' : (getSellerName(id) || '—'), ...v }))
            .sort((a, b) => b.value - a.value);
        const sellerTable = sellerRows.length ? `<section><h4 class="sale">Venduto per commerciale</h4>
            <table><thead><tr><th>Commerciale</th><th>Offerte confermate</th><th>Valore</th></tr></thead>
            <tbody>${sellerRows.map(r => `<tr><td>${esc(r.name)}</td><td>${r.count}</td><td>${esc(formatCurrency(r.value))}</td></tr>`).join('')}</tbody></table></section>` : '';
        const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Resoconto settimana ${esc(rangeLabel)}</title>
            <style>
                body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;}
                h1{font-size:20px;margin:0 0 4px;} .sub{color:#555;margin:0 0 16px;font-size:13px;}
                .totals{display:flex;gap:24px;margin:0 0 20px;font-size:14px;}
                .totals b{font-size:16px;}
                section{margin:0 0 18px;page-break-inside:avoid;}
                h3{font-size:15px;background:#f1f5f9;padding:6px 8px;border-radius:4px;margin:0 0 6px;text-transform:capitalize;}
                h4{font-size:12px;margin:8px 0 4px;text-transform:uppercase;letter-spacing:.5px;}
                h4.del{color:#DC2626;} h4.sale{color:#2563EB;} h4.prod{color:#064E3B;}
                table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:6px;}
                th,td{border:1px solid #e2e8f0;padding:4px 6px;text-align:left;}
                th{background:#f8fafc;}
                .pill{color:#fff;padding:1px 6px;border-radius:9px;font-size:10px;font-weight:bold;}
                td.notes{font-style:italic;color:#555;max-width:260px;}
                @media print{body{margin:12px;}}
            </style></head><body>
            <h1>Resoconto Settimanale — Carrozzeria Pezzaioli</h1>
            <p class="sub">${esc(rangeLabel)}${paymentFilter !== 'all' ? ' · Filtro pagamento: ' + esc(paymentInfo(paymentFilter === 'nonsaldato' ? '' : paymentFilter).label) : ''}</p>
            <div class="totals">
                <span>Veicoli da consegnare: <b>${totalDeliveries}</b></span>
                <span>Offerte confermate: <b>${totalSales}</b></span>
                <span>Inizio produzione: <b>${totalProductions}</b></span>
                <span>Valore offerte confermate: <b>${esc(formatCurrency(totalValue))}</b></span>
            </div>
            ${sellerTable}
            ${daysHtml || '<p>Nessun evento in questa settimana.</p>'}
            </body></html>`;
        const w = window.open('', '_blank');
        if (!w) { toast.error('Abilita i popup per stampare il resoconto'); return; }
        w.document.write(html);
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 400);
    };

    const downloadMonthlyExcel = async () => {
        try {
            const month = format(currentMonth, 'yyyy-MM');
            const userId = (canSeeOwners && sellerFilter !== 'all') ? sellerFilter : undefined;
            const res = await reportsAPI.downloadMonthlyExcel(month, userId);
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Report_Direzione_${month}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Errore nel download del report Excel');
        }
    };

    const printMonthReport = () => {        const esc = (s) => String(s ?? '—').replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c]));
        const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: it });
        let totalDeliveries = 0, totalSales = 0, totalValue = 0, totalProductions = 0;
        const sellerMap = new Map();
        const blank = () => ({ deliveries: 0, sales: 0, value: 0, productions: 0 });
        daysInMonth.forEach(day => {
            const acts = getActivitiesForDate(day);
            const deliveries = acts.filter(a => a.type === 'delivery' && matchesPayment(oppById.get(a.opportunity_id)));
            const sales = acts.filter(a => a.type === 'confirmation' && matchesPayment(oppById.get(a.opportunity_id)));
            const productions = acts.filter(a => a.type === 'production' && matchesPayment(oppById.get(a.opportunity_id)));
            totalDeliveries += deliveries.length;
            totalSales += sales.length;
            totalProductions += productions.length;
            productions.forEach(a => {
                const id = a.assigned_to || 'none';
                const cur = sellerMap.get(id) || blank();
                cur.productions += 1;
                sellerMap.set(id, cur);
            });
            deliveries.forEach(a => {
                const id = a.assigned_to || 'none';
                const cur = sellerMap.get(id) || blank();
                cur.deliveries += 1;
                sellerMap.set(id, cur);
            });
            sales.forEach(a => {
                const v = oppById.get(a.opportunity_id)?.value || 0;
                totalValue += v;
                const id = a.assigned_to || 'none';
                const cur = sellerMap.get(id) || blank();
                cur.sales += 1; cur.value += v;
                sellerMap.set(id, cur);
            });
        });
        const rows = Array.from(sellerMap.entries())
            .map(([id, v]) => ({ name: id === 'none' ? '—' : (getSellerName(id) || '—'), ...v }))
            .sort((a, b) => b.value - a.value);
        const sellerTable = rows.length ? `<table><thead><tr><th>Commerciale</th><th>Offerte confermate</th><th>Valore</th><th>Consegne</th><th>Entrati in produzione</th></tr></thead>
            <tbody>${rows.map(r => `<tr><td>${esc(r.name)}</td><td>${r.sales}</td><td>${esc(formatCurrency(r.value))}</td><td>${r.deliveries}</td><td>${r.productions}</td></tr>`).join('')}
            <tr class="tot"><td>TOTALE</td><td>${totalSales}</td><td>${esc(formatCurrency(totalValue))}</td><td>${totalDeliveries}</td><td>${totalProductions}</td></tr></tbody></table>`
            : '<p>Nessun evento in questo mese.</p>';
        const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Report mensile ${esc(monthLabel)}</title>
            <style>
                body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px;}
                h1{font-size:20px;margin:0 0 4px;} .sub{color:#555;margin:0 0 16px;font-size:13px;text-transform:capitalize;}
                .totals{display:flex;gap:24px;margin:0 0 20px;font-size:14px;} .totals b{font-size:16px;}
                h2{font-size:14px;margin:18px 0 6px;text-transform:uppercase;letter-spacing:.5px;color:#2563EB;}
                table{width:100%;border-collapse:collapse;font-size:13px;}
                th,td{border:1px solid #e2e8f0;padding:6px 8px;text-align:left;}
                th{background:#f8fafc;}
                tr.tot td{font-weight:bold;background:#f1f5f9;}
                @media print{body{margin:12px;}}
            </style></head><body>
            <h1>Report Mensile Direzione — Carrozzeria Pezzaioli</h1>
            <p class="sub">${esc(monthLabel)}${paymentFilter !== 'all' ? ' · Filtro pagamento: ' + esc(paymentInfo(paymentFilter === 'nonsaldato' ? '' : paymentFilter).label) : ''}</p>
            <div class="totals">
                <span>Veicoli consegnati: <b>${totalDeliveries}</b></span>
                <span>Offerte confermate: <b>${totalSales}</b></span>
                <span>Veicoli entrati in produzione: <b>${totalProductions}</b></span>
                <span>Valore offerte confermate: <b>${esc(formatCurrency(totalValue))}</b></span>
            </div>
            <h2>Riepilogo per commerciale</h2>
            ${sellerTable}
            </body></html>`;
        const w = window.open('', '_blank');
        if (!w) { toast.error('Abilita i popup per stampare il report'); return; }
        w.document.write(html);
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 400);
    };


    const selectedDateActivities = getActivitiesForDate(selectedDate);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="calendar-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">Calendario</h1>
                    <p className="text-muted-foreground">
                        {activities.length} attività pianificate
                    </p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button 
                            className="btn-primary font-heading"
                            onClick={handleOpenDialog}
                            data-testid="add-activity-btn"
                        >
                            <Plus size={20} className="mr-2" />
                            NUOVA ATTIVITÀ
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">
                                {editingActivity ? 'Modifica Attività' : 'Nuova Attività'}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Titolo *</Label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    required
                                    data-testid="activity-title"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Tipo *</Label>
                                <Select
                                    value={formData.type}
                                    onValueChange={(value) => setFormData({...formData, type: value})}
                                >
                                    <SelectTrigger data-testid="activity-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ACTIVITY_TYPES.filter((type) => CREATABLE_TYPES.includes(type.id)).map((type) => (
                                            <SelectItem key={type.id} value={type.id}>
                                                <div className="flex items-center gap-2">
                                                    <type.icon size={16} style={{ color: type.color }} />
                                                    {type.label}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Cliente (opzionale)</Label>
                                <ClientCombobox
                                    clients={clients}
                                    value={formData.client_id}
                                    onChange={(value) => setFormData({...formData, client_id: value})}
                                    testid="activity-client"
                                />
                            </div>
                            {canAssign && (
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Commerciale (opzionale)</Label>
                                    <Select
                                        value={formData.assigned_to || 'none'}
                                        onValueChange={(v) => setFormData({...formData, assigned_to: v === 'none' ? '' : v})}
                                    >
                                        <SelectTrigger data-testid="activity-seller">
                                            <SelectValue placeholder="Assegna a un commerciale" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— Io (amministratore) —</SelectItem>
                                            {sellers.map((s) => (
                                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        Per ferie/viaggi seleziona il commerciale a cui si riferisce l'evento.
                                    </p>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Dal *</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start" data-testid="activity-date">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {format(formData.scheduled_at, 'PPP', { locale: it })}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={formData.scheduled_at}
                                                onSelect={(date) => date && setFormData({...formData, scheduled_at: date, end_at: (formData.end_at && formData.end_at < date) ? null : formData.end_at})}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Ora *</Label>
                                    <Input
                                        type="time"
                                        value={formData.scheduled_time}
                                        onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                                        required
                                        data-testid="activity-time"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Al (opzionale)</Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start" data-testid="activity-end-date">
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.end_at
                                                ? format(formData.end_at, 'PPP', { locale: it })
                                                : <span className="text-muted-foreground">Evento di un solo giorno</span>}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formData.end_at || undefined}
                                            disabled={(date) => date < new Date(new Date(formData.scheduled_at).setHours(0, 0, 0, 0))}
                                            onSelect={(date) => setFormData({...formData, end_at: date || null})}
                                        />
                                        {formData.end_at && (
                                            <div className="p-2 border-t">
                                                <Button type="button" variant="ghost" size="sm" className="w-full"
                                                    onClick={() => setFormData({...formData, end_at: null})}
                                                    data-testid="activity-end-date-clear">
                                                    Rimuovi data fine
                                                </Button>
                                            </div>
                                        )}
                                    </PopoverContent>
                                </Popover>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Imposta una data di fine per eventi che durano più giorni (es. viaggio, ferie).
                                </p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Note</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                                    rows={3}
                                    data-testid="activity-notes"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    Annulla
                                </Button>
                                <Button type="submit" className="btn-primary" data-testid="activity-submit">
                                    {editingActivity ? 'SALVA' : 'CREA'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Admin: filtro e legenda commerciali */}
            {canSeeOwners && sellers.length > 0 && (
                <Card data-testid="calendar-seller-legend">
                    <CardContent className="p-4 flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <Label className="text-xs uppercase tracking-wider shrink-0">Commerciale</Label>
                            <Select value={sellerFilter} onValueChange={setSellerFilter}>
                                <SelectTrigger className="w-full sm:w-[240px]" data-testid="calendar-seller-filter">
                                    <SelectValue placeholder="Commerciale" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i commerciali</SelectItem>
                                    {sellers.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Label className="text-xs uppercase tracking-wider shrink-0 sm:ml-4">Tipo attività</Label>
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="w-full sm:w-[240px]" data-testid="calendar-type-filter">
                                    <SelectValue placeholder="Tipo attività" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i tipi</SelectItem>
                                    {ACTIVITY_TYPES.filter((t) => !['call', 'email'].includes(t.id)).map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            <span className="flex items-center gap-2">
                                                <t.icon size={14} style={{ color: t.color }} />
                                                {t.label}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {sellers.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSellerFilter(sellerFilter === s.id ? 'all' : s.id)}
                                    className={`flex items-center gap-2 text-sm transition-opacity ${
                                        sellerFilter !== 'all' && sellerFilter !== s.id ? 'opacity-40' : ''
                                    }`}
                                    data-testid={`calendar-legend-${s.id}`}
                                >
                                    <span
                                        className="w-3 h-3 rounded-full shrink-0"
                                        style={{ backgroundColor: sellerColorMap[s.id] }}
                                    />
                                    {s.name}
                                </button>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Legenda eventi automatici */}
            <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="calendar-auto-legend">
                <span className="text-xs uppercase tracking-wider text-muted-foreground">Eventi offerte:</span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#DC2626' }} />
                    Consegna
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#2563EB' }} />
                    Offerta confermata
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#064E3B' }} />
                    Inizio produzione
                </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Calendar Grid */}
                <Card className={viewMode === 'week' ? 'lg:col-span-3' : 'lg:col-span-2'}>
                    <CardHeader className="pb-2">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <CardTitle className="font-heading uppercase text-lg">
                                {viewMode === 'month'
                                    ? format(currentMonth, 'MMMM yyyy', { locale: it })
                                    : `${format(weekStart, 'd MMM', { locale: it })} - ${format(endOfWeek(currentMonth, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: it })}`}
                            </CardTitle>
                            <div className="flex items-center gap-2">
                                <div className="flex rounded-sm border overflow-hidden mr-1">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('month')}
                                        className={`px-3 py-1 text-xs font-medium ${viewMode === 'month' ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                                        data-testid="calendar-view-month"
                                    >
                                        Mese
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('week')}
                                        className={`px-3 py-1 text-xs font-medium ${viewMode === 'week' ? 'bg-primary text-white' : 'bg-background hover:bg-muted'}`}
                                        data-testid="calendar-view-week"
                                    >
                                        Settimana
                                    </button>
                                </div>
                                {viewMode === 'month' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={printMonthReport}
                                        data-testid="month-report-btn"
                                    >
                                        <Printer size={14} className="mr-2" /> Report Mensile
                                    </Button>
                                )}
                                {viewMode === 'month' && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={downloadMonthlyExcel}
                                        data-testid="month-report-excel-btn"
                                    >
                                        <Download size={14} className="mr-2" /> Excel
                                    </Button>
                                )}
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setCurrentMonth(viewMode === 'month' ? subMonths(currentMonth, 1) : subWeeks(currentMonth, 1))}
                                    data-testid="calendar-prev"
                                >
                                    ←
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setCurrentMonth(new Date())}
                                >
                                    Oggi
                                </Button>
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => setCurrentMonth(viewMode === 'month' ? addMonths(currentMonth, 1) : addWeeks(currentMonth, 1))}
                                    data-testid="calendar-next"
                                >
                                    →
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {viewMode === 'month' ? (
                        <>
                        {/* Days header */}
                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => (
                                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar grid */}
                        <div className="grid grid-cols-7 gap-1">
                            {/* Empty cells for days before month starts */}
                            {Array.from({ length: (startOfMonth(currentMonth).getDay() + 6) % 7 }).map((_, i) => (
                                <div key={`empty-${i}`} className="h-24" />
                            ))}

                            {daysInMonth.map((day) => {
                                const dayActivities = getActivitiesForDate(day);
                                const isSelected = isSameDay(day, selectedDate);
                                const today = isToday(day);

                                return (
                                    <button
                                        key={day.toISOString()}
                                        onClick={() => setSelectedDate(day)}
                                        className={`h-24 p-1 border rounded-sm text-left transition-colors ${
                                            isSelected 
                                                ? 'border-primary bg-primary/5' 
                                                : 'border-border hover:bg-muted/50'
                                        }`}
                                        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                                    >
                                        <div className={`text-sm font-medium mb-1 ${
                                            today ? 'text-primary' : ''
                                        }`}>
                                            {format(day, 'd')}
                                        </div>
                                        <div className="space-y-0.5">
                                            {dayActivities.slice(0, 2).map((activity) => {
                                                const color = activityColor(activity);
                                                const opp = activity.opportunity_id ? oppById.get(activity.opportunity_id) : null;
                                                const TypeIcon = getActivityTypeInfo(activity.type).icon;
                                                let chipLabel = activity.title;
                                                if (activity.type === 'delivery') {
                                                    chipLabel = opp?.title || activity.title.replace(/^Consegna\s*[—-]\s*/i, '');
                                                } else if ((activity.type === 'confirmation' || activity.type === 'production') && opp?.title) {
                                                    chipLabel = opp.title;
                                                }
                                                return (
                                                    <div
                                                        key={activity.id}
                                                        className="flex items-center gap-1 text-xs font-medium truncate px-1.5 py-1 rounded cursor-pointer hover:brightness-95"
                                                        style={{ 
                                                            backgroundColor: `${color}20`,
                                                            borderLeft: `3px solid ${color}`
                                                        }}
                                                        onClick={(e) => { e.stopPropagation(); openDetail(activity); }}
                                                        title={chipLabel}
                                                        data-testid={`month-chip-${activity.id}`}
                                                    >
                                                        <TypeIcon size={14} style={{ color }} className="shrink-0" />
                                                        <span className="truncate">{chipLabel}</span>
                                                    </div>
                                                );
                                            })}
                                            {dayActivities.length > 2 && (
                                                <div className="text-xs text-muted-foreground px-1">
                                                    +{dayActivities.length - 2} altre
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        </>
                        ) : (
                        /* Vista settimanale: resoconto Consegne + Offerte confermate */
                        <div className="space-y-3" data-testid="calendar-week-view">
                            {(() => {
                                let totDel = 0, totSale = 0, totValue = 0, totProd = 0;
                                weekDays.forEach(d => {
                                    const acts = getActivitiesForDate(d);
                                    acts.filter(a => a.type === 'delivery' && matchesPayment(oppById.get(a.opportunity_id))).forEach(() => { totDel += 1; });
                                    acts.filter(a => a.type === 'confirmation' && matchesPayment(oppById.get(a.opportunity_id))).forEach(a => { totSale += 1; totValue += (oppById.get(a.opportunity_id)?.value || 0); });
                                    totProd += acts.filter(a => a.type === 'production' && matchesPayment(oppById.get(a.opportunity_id))).length;
                                });
                                return (
                                    <div className="flex flex-wrap items-center gap-3 pb-1" data-testid="week-summary">
                                        <span className="flex items-center gap-2 text-sm">
                                            <Truck size={16} style={{ color: '#DC2626' }} />
                                            <b>{totDel}</b> da consegnare
                                        </span>
                                        <span className="flex items-center gap-2 text-sm">
                                            <CheckCircle2 size={16} style={{ color: '#2563EB' }} />
                                            <b>{totSale}</b> offerte confermate
                                        </span>
                                        <span className="flex items-center gap-2 text-sm" data-testid="week-total-production">
                                            <Factory size={16} style={{ color: '#064E3B' }} />
                                            <b>{totProd}</b> inizio produzione
                                        </span>
                                        <span className="text-sm px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700" data-testid="week-total-value">
                                            Valore offerte confermate settimanale: <b>{formatCurrency(totValue)}</b>
                                        </span>
                                        <div className="flex flex-wrap items-center gap-2 ml-auto">
                                            <Select value={weekSection} onValueChange={setWeekSection}>
                                                <SelectTrigger className="w-[190px] h-9" data-testid="week-section-filter">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Tutte le sezioni</SelectItem>
                                                    <SelectItem value="delivery">Solo consegne</SelectItem>
                                                    <SelectItem value="confirmation">Solo vendite</SelectItem>
                                                    <SelectItem value="production">Solo inizio produzione</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                                                <SelectTrigger className="w-[170px] h-9" data-testid="week-payment-filter">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">Tutti i pagamenti</SelectItem>
                                                    <SelectItem value="nonsaldato">Non saldato</SelectItem>
                                                    <SelectItem value="acconto">Acconto</SelectItem>
                                                    <SelectItem value="saldato">Saldato</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Button variant="outline" size="sm" onClick={printWeekReport} data-testid="week-print-btn">
                                                <Printer size={14} className="mr-2" /> Stampa / PDF
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })()}
                            {(() => {
                                const map = new Map();
                                weekDays.forEach(day => {
                                    getActivitiesForDate(day)
                                        .filter(a => a.type === 'confirmation' && matchesPayment(oppById.get(a.opportunity_id)))
                                        .forEach(a => {
                                            const id = a.assigned_to || 'none';
                                            const cur = map.get(id) || { count: 0, value: 0 };
                                            cur.count += 1; cur.value += (oppById.get(a.opportunity_id)?.value || 0);
                                            map.set(id, cur);
                                        });
                                });
                                const rows = Array.from(map.entries())
                                    .map(([id, v]) => ({ id, name: id === 'none' ? '—' : (getSellerName(id) || '—'), ...v }))
                                    .sort((a, b) => b.value - a.value);
                                if (rows.length === 0) return null;
                                return (
                                    <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="week-seller-totals">
                                        <span className="text-xs uppercase tracking-wider text-muted-foreground self-center">Venduto per commerciale:</span>
                                        {rows.map(r => (
                                            <span
                                                key={r.id}
                                                className="flex items-center gap-2 text-xs px-2 py-1 rounded-sm border bg-muted/30"
                                                data-testid={`week-seller-total-${r.id}`}
                                            >
                                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: sellerColorMap[r.id] || '#2563EB' }} />
                                                <b>{r.name}</b>
                                                <span className="text-muted-foreground">{r.count} off.</span>
                                                <b className="text-emerald-700">{formatCurrency(r.value)}</b>
                                            </span>
                                        ))}
                                    </div>
                                );
                            })()}
                            {weekDays.map((day) => {
                                const dayActs = getActivitiesForDate(day);
                                const byInsertion = (a, b) => {
                                    const oa = oppById.get(a.opportunity_id)?.created_at || a.created_at || '';
                                    const ob = oppById.get(b.opportunity_id)?.created_at || b.created_at || '';
                                    return new Date(oa) - new Date(ob);
                                };
                                const deliveries = dayActs.filter(a => a.type === 'delivery' && matchesPayment(oppById.get(a.opportunity_id))).sort(byInsertion);
                                const confirmations = dayActs.filter(a => a.type === 'confirmation' && matchesPayment(oppById.get(a.opportunity_id))).sort(byInsertion);
                                const productions = dayActs.filter(a => a.type === 'production' && matchesPayment(oppById.get(a.opportunity_id))).sort(byInsertion);
                                const today = isToday(day);
                                return (
                                    <div
                                        key={day.toISOString()}
                                        className={`border rounded-sm overflow-hidden ${today ? 'border-primary' : 'border-border'}`}
                                        data-testid={`week-day-${format(day, 'yyyy-MM-dd')}`}
                                    >
                                        <div className={`px-3 py-1.5 flex items-center justify-between ${today ? 'bg-primary/10' : 'bg-muted/40'}`}>
                                            <span className="font-heading uppercase text-sm tracking-wider">
                                                {format(day, 'EEEE d MMMM', { locale: it })}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                {deliveries.length} consegne · {confirmations.length} vendite · {productions.length} in produzione
                                            </span>
                                        </div>
                                        <div className={`grid md:divide-x ${weekSection === 'all' ? 'md:grid-cols-3' : 'grid-cols-1'}`}>
                                            {showSec('delivery') && (
                                            <div className="p-2">
                                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#DC2626' }}>
                                                    <Truck size={14} /> Consegne
                                                </div>
                                                {deliveries.length === 0 ? (
                                                    <p className="text-xs text-muted-foreground px-1">Nessuna consegna</p>
                                                ) : (
                                                    <div className="space-y-1">
                                                        {deliveries.map((a) => {
                                                            const opp = oppById.get(a.opportunity_id) || {};
                                                            return (
                                                                <div
                                                                    key={a.id}
                                                                    className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm px-2 py-1 rounded-sm border-l-2 cursor-pointer hover:brightness-95 transition-all"
                                                                    style={{ borderLeftColor: '#DC2626', backgroundColor: '#DC262610' }}
                                                                    onClick={() => openOffer(a.opportunity_id)}
                                                                    title="Apri offerta"
                                                                    data-testid={`week-delivery-${a.id}`}
                                                                >
                                                                    <span className="font-medium">{opp.title || a.title}</span>
                                                                    {(() => { const p = paymentInfo(opp.payment_status); return (
                                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: p.color }} data-testid={`week-delivery-payment-${a.id}`}>{p.label}</span>
                                                                    ); })()}
                                                                    <LateBadge opp={opp} testId={`week-delivery-late-${a.id}`} />
                                                                    {opp.commessa && <span className="text-xs text-muted-foreground">Commessa: <b className="text-foreground">{opp.commessa}</b></span>}
                                                                    {opp.telaio && <span className="text-xs text-muted-foreground">Telaio: <b className="text-foreground">{opp.telaio}</b></span>}
                                                                    <span className="text-xs text-muted-foreground">Venditore: <b className="text-foreground">{getSellerName(a.assigned_to) || '—'}</b></span>
                                                                    <span className="text-xs text-muted-foreground">Cliente: <b className="text-foreground">{getClientName(a.client_id) || '—'}</b></span>
                                                                    {weekNotes(a, opp) && (
                                                                        <span className="basis-full text-xs text-muted-foreground italic" data-testid={`week-delivery-notes-${a.id}`}>Note: <span className="text-foreground not-italic">{weekNotes(a, opp)}</span></span>
                                                                    )}
                                                                    {canWriteRelazione && (
                                                                        <button type="button" onClick={(e) => { e.stopPropagation(); openRelazione(a); }} className="text-xs text-primary hover:underline inline-flex items-center gap-1 print:hidden" data-testid={`week-relazione-${a.id}`}>
                                                                            <NotebookPen size={12} /> Scrivi relazione
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>)}
                                            {showSec('confirmation') && (
                                            <div className="p-2">
                                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#2563EB' }}>
                                                    <CheckCircle2 size={14} /> Offerte confermate
                                                </div>
                                                {confirmations.length === 0 ? (
                                                    <p className="text-xs text-muted-foreground px-1">Nessuna vendita</p>
                                                ) : (
                                                    <div className="space-y-1">
                                                        {confirmations.map((a) => {
                                                            const opp = oppById.get(a.opportunity_id) || {};
                                                            return (
                                                                <div
                                                                    key={a.id}
                                                                    className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm px-2 py-1 rounded-sm border-l-2 cursor-pointer hover:brightness-95 transition-all"
                                                                    style={{ borderLeftColor: '#2563EB', backgroundColor: '#2563EB10' }}
                                                                    onClick={() => openOffer(a.opportunity_id)}
                                                                    title="Apri offerta"
                                                                    data-testid={`week-sale-${a.id}`}
                                                                >
                                                                    <span className="font-medium">{opp.title || a.title}</span>
                                                                    {(() => { const p = paymentInfo(opp.payment_status); return (
                                                                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: p.color }} data-testid={`week-sale-payment-${a.id}`}>{p.label}</span>
                                                                    ); })()}
                                                                    <span className="text-xs text-muted-foreground">Venditore: <b className="text-foreground">{getSellerName(a.assigned_to) || '—'}</b></span>
                                                                    {opp.commessa && <span className="text-xs text-muted-foreground">Commessa: <b className="text-foreground">{opp.commessa}</b></span>}
                                                                    {opp.telaio && <span className="text-xs text-muted-foreground">Telaio: <b className="text-foreground">{opp.telaio}</b></span>}
                                                                    <span className="text-xs text-muted-foreground">Cliente: <b className="text-foreground">{getClientName(a.client_id) || '—'}</b></span>
                                                                    {weekNotes(a, opp) && (
                                                                        <span className="basis-full text-xs text-muted-foreground italic" data-testid={`week-sale-notes-${a.id}`}>Note: <span className="text-foreground not-italic">{weekNotes(a, opp)}</span></span>
                                                                    )}
                                                                    {canWriteRelazione && (
                                                                        <button type="button" onClick={(e) => { e.stopPropagation(); openRelazione(a); }} className="text-xs text-primary hover:underline inline-flex items-center gap-1 print:hidden" data-testid={`week-relazione-${a.id}`}>
                                                                            <NotebookPen size={12} /> Scrivi relazione
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>)}
                                            {showSec('production') && <WeekProductionColumn
                                                items={productions}
                                                oppById={oppById}
                                                getSellerName={getSellerName}
                                                getClientName={getClientName}
                                                weekNotes={weekNotes}
                                                openOffer={openOffer}
                                                onRelazione={canWriteRelazione ? openRelazione : null}
                                            />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </CardContent>
                </Card>

                {/* Selected Day Activities */}
                {viewMode === 'month' && (
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading uppercase text-lg">
                            {format(selectedDate, 'EEEE d MMMM', { locale: it })}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedDateActivities.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <CalendarIcon className="mx-auto h-12 w-12 mb-3 opacity-20" />
                                <p>Nessuna attività per questo giorno</p>
                                <Button 
                                    variant="link" 
                                    onClick={handleOpenDialog}
                                    className="mt-2"
                                >
                                    Aggiungi attività
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {selectedDateActivities
                                    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
                                    .map((activity) => {
                                        const typeInfo = getActivityTypeInfo(activity.type);
                                        const clientName = getClientName(activity.client_id);
                                        const color = activityColor(activity);
                                        const sellerName = canSeeOwners ? getSellerName(activity.assigned_to) : '';
                                        
                                        return (
                                            <div
                                                key={activity.id}
                                                className={`p-3 rounded-sm border-l-4 ${
                                                    activity.completed ? 'bg-muted/30 opacity-60' : 'bg-white'
                                                } ${activity.opportunity_id ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
                                                style={{ borderLeftColor: color }}
                                                onClick={activity.opportunity_id ? () => openOffer(activity.opportunity_id) : undefined}
                                                title={activity.opportunity_id ? 'Apri offerta' : undefined}
                                                data-testid={`activity-item-${activity.id}`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <typeInfo.icon size={16} style={{ color: typeInfo.color }} />
                                                            <span className={`font-medium ${activity.completed ? 'line-through' : ''}`}>
                                                                {activity.title}
                                                            </span>
                                                            {sellerName && (
                                                                <span
                                                                    className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white"
                                                                    style={{ backgroundColor: color }}
                                                                    data-testid={`activity-seller-${activity.id}`}
                                                                    title={sellerName}
                                                                >
                                                                    {initials(sellerName)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                                            <Clock size={12} />
                                                            {format(new Date(activity.scheduled_at), 'HH:mm')}
                                                            {sellerName && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>{sellerName}</span>
                                                                </>
                                                            )}
                                                            {clientName && (
                                                                <>
                                                                    <span>•</span>
                                                                    <span>{clientName}</span>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {canWriteRelazione && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                title="Scrivi relazione"
                                                                onClick={() => openRelazione(activity)}
                                                                data-testid={`activity-relazione-${activity.id}`}
                                                            >
                                                                <NotebookPen size={18} className="text-muted-foreground" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => handleToggleComplete(activity)}
                                                        >
                                                            <CheckCircle2 
                                                                size={18} 
                                                                className={activity.completed ? 'text-green-500' : 'text-muted-foreground'}
                                                            />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-destructive"
                                                            onClick={() => handleDelete(activity.id)}
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </div>
                                                {activity.notes && (
                                                    <p className="text-sm text-muted-foreground mt-2 pl-6">
                                                        {activity.notes}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                            </div>
                        )}
                    </CardContent>
                </Card>
                )}
            </div>

            {/* Anteprima dettagli attività */}
            <Dialog open={!!detailActivity} onOpenChange={(v) => !v && setDetailActivity(null)}>
                <DialogContent className="max-w-md" data-testid="activity-detail-dialog">
                    {detailActivity && (() => {
                        const ti = getActivityTypeInfo(detailActivity.type);
                        const start = new Date(detailActivity.scheduled_at);
                        const end = detailActivity.end_at ? new Date(detailActivity.end_at) : null;
                        const Icon = ti.icon;
                        return (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2 font-heading uppercase">
                                        <Icon size={20} style={{ color: ti.color }} />
                                        {detailActivity.title}
                                    </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3 text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-0.5 rounded-sm text-white text-xs" style={{ backgroundColor: ti.color }}>{ti.label}</span>
                                        {detailActivity.completed && <span className="text-xs text-emerald-600 font-medium">Completata</span>}
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Data: </span>
                                        <b>
                                            {end
                                                ? `dal ${format(start, 'PPP', { locale: it })} al ${format(end, 'PPP', { locale: it })}`
                                                : `${format(start, 'PPP', { locale: it })} — ${format(start, 'HH:mm')}`}
                                        </b>
                                    </div>
                                    {canSeeOwners && (
                                        <div><span className="text-muted-foreground">Commerciale: </span><b>{getSellerName(detailActivity.assigned_to) || '—'}</b></div>
                                    )}
                                    <div><span className="text-muted-foreground">Cliente: </span><b>{getClientName(detailActivity.client_id) || '—'}</b></div>
                                    {detailActivity.notes && (
                                        <div><span className="text-muted-foreground">Note: </span>{detailActivity.notes}</div>
                                    )}
                                </div>
                                <div className="flex flex-wrap justify-end gap-2 pt-3">
                                    {canWriteRelazione && (
                                        <Button variant="outline" className="mr-auto" onClick={() => { const a = detailActivity; setDetailActivity(null); openRelazione(a); }} data-testid="detail-write-relazione">
                                            <NotebookPen size={14} className="mr-2" /> Scrivi relazione
                                        </Button>
                                    )}
                                    {detailActivity.opportunity_id ? (
                                        <Button variant="outline" onClick={() => { const id = detailActivity.opportunity_id; setDetailActivity(null); openOffer(id); }} data-testid="detail-open-offer">
                                            Apri offerta
                                        </Button>
                                    ) : (
                                        <>
                                            <Button variant="outline" onClick={() => { const a = detailActivity; setDetailActivity(null); openEditActivity(a); }} data-testid="detail-edit">
                                                Modifica
                                            </Button>
                                            <Button variant="destructive" onClick={() => { const id = detailActivity.id; setDetailActivity(null); handleDelete(id); }} data-testid="detail-delete">
                                                Elimina
                                            </Button>
                                        </>
                                    )}
                                    <Button onClick={() => setDetailActivity(null)} data-testid="detail-close">Chiudi</Button>
                                </div>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CalendarPage;
